// Adaptors
// Constants
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { MruApp } from "@ms/planner-models/lib/local/service/mru/MruApp";
import { ChildView, ChildViewStatus } from "@ms/uno-constants/lib/local/ChildViewConstants";
// Premium Plan
import { premiumPlanInstantiateLoader } from "@ms/planner-childview/lib/local/controls/adaptors/premiumPlanViewAdaptor/PremiumPlanLoaders";
// Portfolio
import { portfolioInstantiateLoader } from "@ms/planner-childview/lib/local/controls/adaptors/portfolioViewAdaptor/portfolioViewLazyLoader";
// Utilities
import { AssetLoadingError } from "@ms/uno-errors/lib/local/errors/AssetLoadingError";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { generateLogStringFromError } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { Worker } from "../Worker";
export class ChildViewInstantiationWorker {
    /** The name of the worker */ get name() {
        return Worker.ChildViewInstantiation;
    }
    /**
     * Initialize the worker
     */ async init() {
        if (!this.initialized) {
            // Register the callback with the org store
            this.storeSubscriptionManager.registerSubscription([
                Store.Mru,
                Store.Route,
                Store.ChildView
            ], ()=>{
                this.tryInitializePremiumPlan();
                this.tryInitializePortfolio();
            });
            this.initialized = true;
            // do an initial pass to initialize the premium plan if a store was hydrated from cache
            await this.tryInitializePremiumPlan();
            // do an initial pass to initialize the portfolio if a store was hydrated from cache
            await this.tryInitializePortfolio();
        }
    }
    /**
     * Handler to try and initialize the premium plan if needed based on the mru and route store
     */ async tryInitializePremiumPlan() {
        // Do not instantiate premium plan if Dataverse is disabled
        if (this.configProvider().settings.disableDataverse) {
            return;
        }
        const [mruStore, routeStore, childViewStore] = this.storeLoader.loadAll([
            Store.Mru,
            Store.Route,
            Store.ChildView
        ]);
        // if mru store is updated, check if there are pinned items
        const pinnedItems = mruStore?.getPinnedItems().data.filter((mruItem)=>mruItem.app === MruApp.Project) ?? [];
        // if route store is updated, check if the current sub route is premium plan
        const currentSubRouteType = routeStore?.getCurrentSubRouteType();
        // if child view store is updated, check if the premium plan view needs to be re-instantiated
        const premiumPlanViewInstantiationStatus = childViewStore?.getChildViewInstantiationStatus(ChildView.PremiumPlan);
        // Reset the instantiation started flag if the view is already instantiated or errored
        if (premiumPlanViewInstantiationStatus === ChildViewStatus.Instantiated || premiumPlanViewInstantiationStatus === ChildViewStatus.Error) {
            this.premiumPlanInstantiationStarted = false;
        }
        // if there are pinned premium plan items or the current sub route is premium plan,
        // or the premium plan instantiation status indicates it needs re instantiate,
        // instantiate premium plan
        if (pinnedItems.length > 0 || currentSubRouteType === SubRouteType.PremiumPlan || currentSubRouteType === SubRouteType.ConfigTeamsTab || premiumPlanViewInstantiationStatus === ChildViewStatus.ReInstantiate) {
            await this.populateViewInstanceConfig();
            await this.instantiatePremiumPlan(false);
        }
    }
    /**
     * Instantiate premium plan view
     * @param isDevMode Indicates if the premium plan is being instantiated in dev mode
     */ async instantiatePremiumPlan(isDevMode) {
        const premiumPlanViewInstantiationStatus = this.storeLoader.load(Store.ChildView)?.getChildViewInstantiationStatus(ChildView.PremiumPlan);
        // If premium plan is already instantiated or in the process of being instantiated, don't instantiate again
        if (this.premiumPlanInstantiationStarted || premiumPlanViewInstantiationStatus === ChildViewStatus.Instantiated || premiumPlanViewInstantiationStatus === ChildViewStatus.InstantiationStarted || premiumPlanViewInstantiationStatus === ChildViewStatus.ScriptsLoaded || premiumPlanViewInstantiationStatus === ChildViewStatus.Error) {
            return;
        }
        this.premiumPlanInstantiationStarted = true;
        const childViewAc = this.actionCreatorLoader.load(ActionCreator.ChildView);
        this.loggers.performanceMarker.mark(MarkerTag.ChildAppInstantiation, {
            viewName: ChildView.PremiumPlan,
            state: ChildViewStatus.InstantiationStarted
        });
        // Load scripts
        let premiumPlanViewInstantiator;
        try {
            premiumPlanViewInstantiator = await premiumPlanInstantiateLoader();
        } catch (e) {
            const err = new AssetLoadingError(e.name, e.message);
            this.loggers.traceLogger.logTrace(0x1e101320 /* tag_4ebm6 */ , TraceLevel.Warning, `[ErrorType=${err.name}][Error=${err.message}]`);
            childViewAc.updateChildViewInstantiationStatus(ChildView.PremiumPlan, ChildViewStatus.Error);
            this.scheduleRetryInstantiation(ChildView.PremiumPlan);
            return;
        }
        // Instantiate premium plan
        try {
            if (isDevMode) {
                await premiumPlanViewInstantiator.premiumPlanDevViewInstantiator.instantiate(this.viewInstanceConfig);
            } else {
                await premiumPlanViewInstantiator.premiumPlanViewInstantiator.instantiate(this.viewInstanceConfig);
            }
            childViewAc.updateChildViewInstantiationStatus(ChildView.PremiumPlan, ChildViewStatus.Instantiated);
            this.loggers.performanceMarker.mark(MarkerTag.ChildAppInstantiation, {
                viewName: ChildView.PremiumPlan,
                state: ChildViewStatus.Instantiated
            });
        } catch (e) {
            // Temporary logging to solve why we're getting empty errors here
            this.loggers.traceLogger.logTrace(0x1e18569f /* tag_4gf05 */ , TraceLevel.Warning, `InitializationError - ${generateLogStringFromError(e)}`);
            // Don't throw error, show view's error state to user
            childViewAc.updateChildViewInstantiationStatus(ChildView.PremiumPlan, ChildViewStatus.Error);
            this.scheduleRetryInstantiation(ChildView.PremiumPlan);
        }
    }
    /**
     * Handler to try and initialize the Portfolio if needed based on the mru and route store
     */ async tryInitializePortfolio() {
        // Do not instantiate portfolio if Dataverse is disabled
        if (this.configProvider().settings.disableDataverse) {
            return;
        }
        const [mruStore, routeStore, childViewStore] = this.storeLoader.loadAll([
            Store.Mru,
            Store.Route,
            Store.ChildView
        ]);
        // if mru store is updated, check if there are pinned Portfolio items
        const pinnedItems = mruStore?.getPinnedItems().data.filter((mruItem)=>mruItem.app === MruApp.Portfolio) ?? [];
        // if route store is updated, check if the current sub route is portfolio
        const currentSubRouteType = routeStore?.getCurrentSubRouteType();
        // if child view store is updated, check if the Portfolio view needs to be re-instantiated
        const portfolioInstantiationStatus = childViewStore?.getChildViewInstantiationStatus(ChildView.Portfolio);
        // Reset the instantiation started flag if the view is already instantiated or errored
        if (portfolioInstantiationStatus === ChildViewStatus.Instantiated || portfolioInstantiationStatus === ChildViewStatus.Error) {
            this.portfolioInstantiationStarted = false;
        }
        // if there are pinned Portfolio items or the current sub route is Portfolio,
        // or the Portfolio instantiation status indicates it needs re instantiate,
        // instantiate Portfolio
        if (pinnedItems.length > 0 || currentSubRouteType === SubRouteType.Portfolio || portfolioInstantiationStatus === ChildViewStatus.ReInstantiate) {
            await this.populateViewInstanceConfig();
            await this.instantiatePortfolio(false);
        }
    }
    /**
     * Instantiate Portfolio view
     * @param isDevMode Indicates if Portfolio is being instantiated in dev mode
     */ async instantiatePortfolio(isDevMode) {
        const portfolioViewInstantiationStatus = this.storeLoader.load(Store.ChildView)?.getChildViewInstantiationStatus(ChildView.Portfolio);
        // If Portfolio is already instantiated or in the process of being instantiated, don't instantiate again
        if (this.portfolioInstantiationStarted || portfolioViewInstantiationStatus === ChildViewStatus.Instantiated || portfolioViewInstantiationStatus === ChildViewStatus.InstantiationStarted || portfolioViewInstantiationStatus === ChildViewStatus.ScriptsLoaded || portfolioViewInstantiationStatus === ChildViewStatus.Error) {
            return;
        }
        this.portfolioInstantiationStarted = true;
        const childViewAc = this.actionCreatorLoader.load(ActionCreator.ChildView);
        this.loggers.performanceMarker.mark(MarkerTag.ChildAppInstantiation, {
            viewName: ChildView.Portfolio,
            state: ChildViewStatus.InstantiationStarted
        });
        // Load scripts
        let portfolioViewInstantiator;
        try {
            portfolioViewInstantiator = await portfolioInstantiateLoader();
        } catch (e) {
            const err = new AssetLoadingError(e.name, e.message);
            this.loggers.traceLogger.logTrace(0x1e10131f /* tag_4ebm5 */ , TraceLevel.Warning, `[ErrorType=${err.name}][Error=${err.message}]`);
            childViewAc.updateChildViewInstantiationStatus(ChildView.Portfolio, ChildViewStatus.Error);
            this.scheduleRetryInstantiation(ChildView.Portfolio);
            return;
        }
        try {
            if (isDevMode) {
                await portfolioViewInstantiator.portfolioDevViewInstantiator.instantiate(this.viewInstanceConfig);
            } else {
                await portfolioViewInstantiator.portfolioViewInstantiator.instantiate(this.viewInstanceConfig);
            }
            childViewAc.updateChildViewInstantiationStatus(ChildView.Portfolio, ChildViewStatus.Instantiated);
            this.loggers.performanceMarker.mark(MarkerTag.ChildAppInstantiation, {
                viewName: ChildView.Portfolio,
                state: ChildViewStatus.Instantiated
            });
        } catch (e) {
            // Temporary logging to solve why we're getting empty errors here
            this.loggers.traceLogger.logTrace(0x1e15b598 /* tag_4f1wy */ , TraceLevel.Warning, `InitializationError - ${generateLogStringFromError(e)}`);
            childViewAc.updateChildViewInstantiationStatus(ChildView.Portfolio, ChildViewStatus.Instantiated);
            this.scheduleRetryInstantiation(ChildView.Portfolio);
        }
    }
    /**
     * Schedules a retry for the view that failed to instantiate
     * Todo: Bug 9769212: Use retry util in ChildAppInstantiationWorker
     * @param childView The child view that failed to instantiate
     */ async scheduleRetryInstantiation(childView) {
        const childViewAc = this.actionCreatorLoader.load(ActionCreator.ChildView);
        const instantiationSettings = this.configProvider().settings.instantiation;
        this.retryCounts[childView] = this.retryCounts[childView] ?? 0;
        if (this.retryCounts[childView] < instantiationSettings.maxRetryCount) {
            this.retryCounts[childView]++;
            setTimeout(()=>{
                childViewAc.updateChildViewInstantiationStatus(childView, ChildViewStatus.Instantiated);
            }, instantiationSettings.retryIntervalMs);
            this.loggers.traceLogger.logTrace(0x1e18569e /* tag_4gf04 */ , TraceLevel.Info, `InitializationError - Child view failed to instantiate. Retrying instantiation. [ChildView=${childView}] [RetryCount=${this.retryCounts[childView]}]`);
        } else {
            this.loggers.traceLogger.logTrace(0x1e18569d /* tag_4gf03 */ , TraceLevel.Error, `InitializationError - Child view failed to instantiate and won't retry. [ChildView=${childView}] [RetryCount=${this.retryCounts[childView]}]`);
        }
    }
    /**
     * Helper method to populate the view instance config
     */ async populateViewInstanceConfig() {
        const appContext = this.storeLoader.load(Store.AppContext)?.getAppContext();
        this.viewInstanceConfig = {
            actionCreatorLoader: this.actionCreatorLoader,
            context: appContext,
            logicModuleLoader: this.logicModuleLoader,
            serviceProviders: this.serviceProviders,
            storeLoader: this.storeLoader,
            loggers: this.loggers,
            configProvider: this.configProvider,
            hostAdaptor: this.hostAdaptor,
            storeSubscriptionManager: this.storeSubscriptionManager,
            authModule: this.authModule
        };
    }
    /**
     * Constructor for the ChildViewInstantiationWorker
     * @param actionCreatorLoader The action creator loader
     * @param storeLoader The store loader
     * @param configProvider The config provider
     * @param loggers The loggers context
     * @param serviceProviders The service providers
     * @param logicModuleLoader The logic module loader
     * @param hostAdaptor The host adaptor
     * @param storeSubscriptionManager The store subscription manager
     * @param authModule The auth module
     */ constructor(actionCreatorLoader, storeLoader, configProvider, loggers, serviceProviders, logicModuleLoader, hostAdaptor, storeSubscriptionManager, authModule){
        this.actionCreatorLoader = actionCreatorLoader;
        this.storeLoader = storeLoader;
        this.configProvider = configProvider;
        this.loggers = loggers;
        this.serviceProviders = serviceProviders;
        this.logicModuleLoader = logicModuleLoader;
        this.hostAdaptor = hostAdaptor;
        this.storeSubscriptionManager = storeSubscriptionManager;
        this.authModule = authModule;
        this.initialized = false;
        /** Indicates if premium plan instantiation has started */ this.premiumPlanInstantiationStarted = false;
        /** Indicates if Portfolio instantiation started */ this.portfolioInstantiationStarted = false;
        /** Number of times to retry instantiation for PremiumPlan and Portfolio */ this.retryCounts = {
            [ChildView.PremiumPlan]: 0,
            [ChildView.Portfolio]: 0
        };
    }
}
